import { FeatureResponse } from "@9amhealth/openapi";
import FunnelKey from "src/constants/funnelKey";
import { FUNNEL_QUESTIONNAIRE_ELIGIBILITY } from "src/constants/misc";
import {
  SignupCustomCampaign,
  SignupCustomCampaignName
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import FactorySignupCustomCampaign from "src/ui/components/SignupCustomContent/campaigns/FactorySignupCustomCampaign";
import { SignupCustomCampaignCDCDPP } from "./SignupCustomCampaignCDCDPP";
import { CalendarDateTime } from "@internationalized/date";
import StateOfGeorgiaIneligibilityContent from "./custom/StateOfGeorgiaIneligibilityContent";
import {
  EligibilityEmployerChoiceId,
  eligibilityEmployerChoiceValues,
  EligibilityHealthPlanChoiceId,
  eligibilityHealthPlanChoiceValues,
  EligibilityQuestionId
} from "./EligibilityQuestionnaireFields";
import envVariables from "src/lib/envVariables";

let instancesCache: SignupCustomCampaign[] | null = null;
let factoryInstancesCache: FactorySignupCustomCampaign[] | null = null;

const campaignInstances: () => SignupCustomCampaign[] = () => {
  if (instancesCache) {
    return instancesCache;
  }
  const instances = [
    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Solera,
      prettyName: "Solera",
      funnelKey: FunnelKey.solera,
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.healthPlan,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityHealthPlanChoiceId.solera,
              value:
                eligibilityHealthPlanChoiceValues[
                  EligibilityHealthPlanChoiceId.solera
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.StateOfIndiana,
      prettyName: "State of Indiana",
      funnelKey: FunnelKey.stateOfIndiana,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/536581e6692a17af77dc2c15085a9e3b019c5668-3840x848.png`,
      logoSize: "large",
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.stateOfIndiana,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.stateOfIndiana
                ]
            }
          },
          {
            questionId: EligibilityQuestionId.healthPlan,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityHealthPlanChoiceId.anthemIndiana,
              value:
                eligibilityHealthPlanChoiceValues[
                  EligibilityHealthPlanChoiceId.anthemIndiana
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Evernorth,
      prettyName: "Evernorth",
      contactForIneligibilityFileCheck: "9am",
      funnelKey: FunnelKey.evernorth,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/eca72b7fe025eb6e20dc82459a575706d2666423-438x77.svg`,
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.healthPlan,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityHealthPlanChoiceId.evernorth,
              value:
                eligibilityHealthPlanChoiceValues[
                  EligibilityHealthPlanChoiceId.evernorth
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.KwikTrip,
      prettyName: "Kwik Trip",
      funnelKey: FunnelKey.kwikTrip,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/cc79972cfb5a8c427e39373deae3f50d4369cd66-375x60.png`,
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.kwikTrip,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.kwikTrip
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Prescryptive,
      prettyName: "Prescryptive",
      funnelKey: FunnelKey.prescryptive,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/ef478da257d2f1cbc3482d519d243617ab42e211-349x138.webp`,
      filterAnswerOptions: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            includeOptions: [
              EligibilityEmployerChoiceId.firstChoice,
              EligibilityEmployerChoiceId.generalPlastics,
              EligibilityEmployerChoiceId.prescryptive
            ]
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Universal,
      prettyName: "Universal",
      funnelKey: FunnelKey.universal,
      getSubscriptionFromSuggestedTreatmentPlan: true,
      personalisedFinalSuccessScreen: true
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Lilly,
      prettyName: "Lilly",
      funnelKey: FunnelKey.lilly,
      getSubscriptionFromSuggestedTreatmentPlan: true,
      personalisedFinalSuccessScreen: true
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.SmithRx,
      prettyName: "SmithRx",
      funnelKey: FunnelKey.smithrx,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/ebe6b7808efc33fe80bb8a5259f867965cc656ca-180x34.svg`,
      filterAnswerOptions: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            includeOptions: [
              EligibilityEmployerChoiceId.airLife,
              EligibilityEmployerChoiceId.albar,
              EligibilityEmployerChoiceId.americanCollegeOfRheumatology,
              EligibilityEmployerChoiceId.celiaCorporation,
              EligibilityEmployerChoiceId.eptam,
              EligibilityEmployerChoiceId.exceptionalMotorcars,
              EligibilityEmployerChoiceId.horizonHealthPartners,
              EligibilityEmployerChoiceId.hydacTechnologyCorporation,
              EligibilityEmployerChoiceId.jimSchmidtChevrolet,
              EligibilityEmployerChoiceId.jjSafety,
              EligibilityEmployerChoiceId.kineticGroup,
              EligibilityEmployerChoiceId.knipper,
              EligibilityEmployerChoiceId.kwestGroup,
              EligibilityEmployerChoiceId.lochmuellerGroup,
              EligibilityEmployerChoiceId.managementMcoa,
              EligibilityEmployerChoiceId.matrixMedical,
              EligibilityEmployerChoiceId.northfieldHospitalAndClinics,
              EligibilityEmployerChoiceId.seveeAndMaherEngineers,
              EligibilityEmployerChoiceId.silverstoneLiving,
              EligibilityEmployerChoiceId.sorensonCommunications,
              EligibilityEmployerChoiceId.unitedDerm,
              EligibilityEmployerChoiceId.unitedTrailers,
              EligibilityEmployerChoiceId.weAreBlood,
              EligibilityEmployerChoiceId.whirleyIndustries,
              EligibilityEmployerChoiceId.wickBuildings
            ]
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Wipfli,
      prettyName: "Wipfli",
      funnelKey: FunnelKey.wipfli,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/6d4ff0f3fc17c198204eb23366484630328d0437-924x301.png`,
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.wipfli,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.wipfli
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Transcarent,
      prettyName: "Transcarent",
      funnelKey: FunnelKey.transcarent,
      availabilityChecks: {
        blockAnonymousUsers: true,
        textContext: "error"
      },
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.healthPlan,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityHealthPlanChoiceId.transcarent,
              value:
                eligibilityHealthPlanChoiceValues[
                  EligibilityHealthPlanChoiceId.transcarent
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Amazon,
      prettyName: "Amazon",
      funnelKey: FunnelKey.amazon,
      availabilityChecks: {
        featureFlag: "AMAZON_WEIGHTLOSS" as FeatureResponse.feature,
        textContext: "notAvailableYet"
      },
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/93fb16874daaebdb21161feaecca6812dbf51473-739x129.svg`,
      logoSize: "large",
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.amazon,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.amazon
                ]
            }
          }
        ]
      }
    }),

    new SignupCustomCampaignCDCDPP(),
    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.LillyTeam,
      prettyName: "Lilly Team",
      funnelKey: FunnelKey.lillyTeam,
      logo: `${envVariables.CMS_IMAGE_PROXY_URL}/534da4ef5cfc1e325a159785ba16c741fcfc3d0a-631x344.svg`,
      logoSize: "small",
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.lilly,
              value: "Lilly"
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.Demo,
      prettyName: "Demo",
      funnelKey: FunnelKey.demo,
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.demo,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.demo
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.NovoCare,
      prettyName: "NovoCare",
      funnelKey: FunnelKey.novocare
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.CignaIfp,
      prettyName: "Cigna IFP",
      funnelKey: FunnelKey.cignaIfp,
      availabilityChecks: {
        releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
      },
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.healthPlan,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityHealthPlanChoiceId.cignaIfp,
              value:
                eligibilityHealthPlanChoiceValues[
                  EligibilityHealthPlanChoiceId.cignaIfp
                ]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.BJs,
      prettyName: "BJ's",
      funnelKey: FunnelKey.bjs,
      contactForIneligibilityFileCheck: "employer",
      availabilityChecks: {
        releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
      },
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.bjs,
              value:
                eligibilityEmployerChoiceValues[EligibilityEmployerChoiceId.bjs]
            }
          }
        ]
      }
    }),

    new FactorySignupCustomCampaign({
      name: SignupCustomCampaignName.stateOfGeorgia,
      prettyName: "State of Georgia",
      funnelKey: FunnelKey.stateOfGeorgia,
      signupContactNumber: "8888709542",
      contactForIneligibilityFileCheck: "9am",
      additionalLegalTextKey: "signup.legalText_stateOfGeorgia",
      fileCheckIneligibilityAdditionalInfo: StateOfGeorgiaIneligibilityContent,
      availabilityChecks: {
        releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
      },
      answerOverrides: {
        [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
          {
            questionId: EligibilityQuestionId.employer,
            fieldType: "choice",
            fieldValue: {
              choiceId: EligibilityEmployerChoiceId.stateOfGeorgia,
              value:
                eligibilityEmployerChoiceValues[
                  EligibilityEmployerChoiceId.stateOfGeorgia
                ]
            }
          }
        ]
      }
    })
  ];
  factoryInstancesCache = instances.filter(
    (i): i is FactorySignupCustomCampaign =>
      i instanceof FactorySignupCustomCampaign
  );
  instancesCache = instances.map((instance) => instance.get());
  return instancesCache;
};

const factoryCampaignInstances = (): FactorySignupCustomCampaign[] => {
  if (!factoryInstancesCache) {
    campaignInstances();
  }
  return factoryInstancesCache as FactorySignupCustomCampaign[];
};

export { campaignInstances, factoryCampaignInstances };
